import React, { useContext, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  GridSize,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import Header from '../components/Header';
import Section from '../components/Section';
import Banner from '../components/Banner';
import PreFooter from '../components/PreFooter';
import Footer from '../components/Footer';
import { authUrl } from '../config/app';
import { auth, database } from '../services/firebase';
import { fetch as fetchLeaderboard, IRanking } from '../services/leaderboard';
import UserContext from '../contexts/UserContext';

import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import DocumentElement from '../components/DocumentElement';
import { fetchDocumentURLArray } from '../services/document';
import PrePostTestDialog from '../components/PrePostTestDialog';
import { preTestCollection, postTestCollection } from '../config/preposttest';

interface IPrepostProps {
  open: boolean;
  type: string;
  preScore: number;
  preTestAnswers: IAnswer[];
  postTestAnswers: IAnswer[];
  end: boolean;
}

const useStyles = makeStyles(() => ({
  board: {
    backgroundColor: '#454057',
    borderRadius: 8,
    paddingBottom: 16,
  },
  boardHeader: {
    backgroundColor: '#564F70',
    borderRadius: '8px 8px 0px 0px',
    marginBottom: 16,
    padding: `16px`,
  },
  boardItem: {
    alignItems: 'center',
    backgroundColor: '#564F70',
    borderRadius: '0px 8px 8px 0px',
    display: 'flex',
    marginBottom: 8,
    marginRight: 8,
    padding: `8px 16px`,
    minHeight: 64,
  },
}));

const leaderboardLength = 30;

const Game = () => {
  const { board, boardHeader, boardItem } = useStyles();
  const { state } = useContext(UserContext);
  const [docUrls, setDocUrls] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<IRanking[]>([]);
  const [prePostState, setPrePostState] = useState<IPrepostProps>({
    open: false,
    type: 'pre',
    preScore: 0,
    preTestAnswers: [],
    postTestAnswers: [],
    end: false,
  });

  useEffect(() => {
    if (state.user) {
      const fetchData = async () => {
        const [
          urlArr,
          leaderboard,
          preTestAnswers,
          postTestAnswers,
          stageSnapshot,
        ] = await Promise.all([
          fetchDocumentURLArray(state.user!.uid),
          fetchLeaderboard(leaderboardLength),
          getPreTest(),
          getPostTest(),
          database.collection('scores').doc(state.user!.uid).get(),
        ]);
        setDocUrls(urlArr);
        setLeaderboard(leaderboard);
        setPrePostState((state) => ({
          ...state,
          preScore:
            preTestAnswers.length > 0
              ? preTestAnswers
                  .map((answer) => (answer.correct ? 1 : 0) as number)
                  .reduce((sum: number, current: number) => sum + current)
              : 0,
          preTestAnswers,
          postTestAnswers,
          end:
            stageSnapshot.exists && stageSnapshot.data()!['chapter8']
              ? true
              : false,
        }));
      };
      fetchData();
    }
  }, [state.user]);
  if (state.loading) {
    return <></>;
  }
  if (!state.user)
    return (
      <Redirect
        to={{
          pathname: '/login',
          search: queryString.stringify({ redirect_url: '/game' }),
        }}
      />
    );
  const getPreTest = async (): Promise<IAnswer[]> => {
    const snapshot = await database
      .collection(preTestCollection)
      .doc(state.user!.uid)
      .get();
    if (snapshot.exists) {
      return snapshot.data()!.answers;
    }
    return [];
  };
  const getPostTest = async (): Promise<IAnswer[]> => {
    const snapshot = await database
      .collection(postTestCollection)
      .doc(state.user!.uid)
      .get();
    if (snapshot.exists) {
      return snapshot.data()!.answers;
    }
    return [];
  };

  const handleClickPlay = async () => {
    const preTest = await getPreTest();
    if (preTest.length > 0) {
      const token = await auth.currentUser?.getIdToken();
      window.open(`${authUrl}/?token=${token}`);
    } else {
      setPrePostState((state) => ({ ...state, open: true, type: 'pre' }));
    }
  };

  const onTestEnd = async () => {
    if (prePostState.type === 'pre') {
      const preTestAnswers = await getPreTest();
      setPrePostState((state) => ({
        ...state,
        preScore:
          preTestAnswers.length > 0
            ? preTestAnswers
                .map((answer) => (answer.correct ? 1 : 0) as number)
                .reduce((sum: number, current: number) => sum + current)
            : 0,
        preTestAnswers,
      }));
    } else {
      const postTestAnswers = await getPostTest();
      setPrePostState((state) => ({
        ...state,
        postTestAnswers,
      }));
    }
  };

  const documents = (
    <>
      <Typography variant="h6">เอกสาร</Typography>
      <Box height={16} />
      <Grid container spacing={1}>
        {docUrls.map((url, index) => {
          return (
            url && (
              <Grid key={index} item xs={12} md={(12 / 3) as GridSize}>
                <DocumentElement name={`Chapter ${index + 1}`} url={url} />
              </Grid>
            )
          );
        })}
      </Grid>
    </>
  );
  return (
    <>
      <Header />
      <Container maxWidth="md">
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Banner onButtonClick={handleClickPlay} />
              <Box height={16} />
              <Box display="flex" justifyContent="space-around">
                <Box>
                  <Typography>
                    ผลทดสอบก่อนเล่น : {prePostState.preScore}
                  </Typography>
                </Box>
                <Box>
                  <Typography>
                    ผลทดสอบหลังเล่น :{' '}
                    {prePostState.postTestAnswers.length > 0 ? (
                      prePostState.postTestAnswers
                        .map((answer) => (answer.correct ? 1 : 0) as number)
                        .reduce((sum: number, current: number) => sum + current)
                    ) : prePostState.end ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          setPrePostState((state) => ({
                            ...state,
                            open: true,
                            type: 'post',
                          }))
                        }
                      >
                        ทำแบบทดสอบ
                      </Button>
                    ) : (
                      0
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box height={16} />
              {docUrls.some((v) => v) && documents}
            </Grid>
            <Grid item xs={12} md={4}>
              <div className={board}>
                <div className={boardHeader}>
                  <Typography>LEADERBOARD</Typography>
                </div>
                <div style={{ height: 500, overflowY: 'auto' }}>
                  {leaderboard.map((info: IRanking, index: number) => (
                    <div key={index} className={boardItem}>
                      <Avatar sizes="small">{index + 1}</Avatar>
                      <Box
                        display="flex"
                        ml={1}
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography>{info.name || 'N/A'}</Typography>
                        <Typography>{info.score}</Typography>
                      </Box>
                    </div>
                  ))}
                </div>
              </div>
            </Grid>
          </Grid>
        </Section>
        <PrePostTestDialog
          open={prePostState.open}
          onClose={() =>
            setPrePostState((state) => ({ ...state, open: false }))
          }
          onTestEnd={onTestEnd}
          type={prePostState.type}
          preScore={prePostState.preScore}
        />
      </Container>
      <PreFooter />
      <Footer />
    </>
  );
};

export default Game;
